// src/components/Supplier/SupplierOffersManager.jsx
import React, { useState, useEffect } from 'react';
import { 
  Tag, 
  Plus, 
  Edit3, 
  Trash2, 
  Calendar, 
  ExternalLink, 
  Image, 
  X, 
  Save, 
  Loader2 
} from 'lucide-react';
import './SupplierOffersManager.css';

const API_BASE_URL = "https://api.lbnhyderabad.com" || 'https://api.lbnhyderabad.com';

export default function SupplierOffersManager({ supplierId }) {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    title: '',
    offer_category: 'Commercial Discount',
    discount_tag: '',
    promo_code: '',
    description: '',
    terms_conditions: '',
    valid_from: new Date().toISOString().split('T')[0],
    valid_until: '',
    offer_url: '',
    status: 'active'
  });
  const [selectedImages, setSelectedImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  useEffect(() => {
    fetchOffers();
  }, [supplierId]);

  const fetchOffers = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/offers/supplier/${supplierId}`);
      const json = await res.json();
      if (json.success) setOffers(json.data);
    } catch (err) {
      console.error('Failed to load offers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (offer = null) => {
    if (offer) {
      setEditingOffer(offer);
      setFormData({
        title: offer.title,
        offer_category: offer.offer_category || 'Commercial Discount',
        discount_tag: offer.discount_tag,
        promo_code: offer.promo_code || '',
        description: offer.description,
        terms_conditions: offer.terms_conditions || '',
        valid_from: offer.valid_from ? offer.valid_from.split('T')[0] : '',
        valid_until: offer.valid_until ? offer.valid_until.split('T')[0] : '',
        offer_url: offer.offer_url || '',
        status: offer.status
      });
      setExistingImages(offer.images || []);
    } else {
      setEditingOffer(null);
      setFormData({
        title: '',
        offer_category: 'Commercial Discount',
        discount_tag: '',
        promo_code: '',
        description: '',
        terms_conditions: '',
        valid_from: new Date().toISOString().split('T')[0],
        valid_until: '',
        offer_url: '',
        status: 'active'
      });
      setExistingImages([]);
    }
    setSelectedImages([]);
    setModalOpen(true);
  };

  const handleDeleteOffer = async (id) => {
    if (!window.confirm('Are you sure you want to delete this offer?')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/offers/${id}`, { method: 'DELETE' });
      const json = await res.json();
      if (json.success) {
        setOffers(prev => prev.filter(o => o.id !== id));
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('supplier_id', supplierId);
      Object.keys(formData).forEach(k => payload.append(k, formData[k]));

      if (editingOffer) {
        payload.append('existingImages', JSON.stringify(existingImages));
      }

      for (let i = 0; i < selectedImages.length; i++) {
        payload.append('offerImages', selectedImages[i]);
      }

      const url = editingOffer 
        ? `${API_BASE_URL}/api/offers/${editingOffer.id}` 
        : `${API_BASE_URL}/api/offers`;
      const method = editingOffer ? 'PUT' : 'POST';

      const res = await fetch(url, { method, body: payload });
      const json = await res.json();

      if (json.success) {
        setModalOpen(false);
        fetchOffers();
      } else {
        alert(json.message || 'Error saving offer.');
      }
    } catch (err) {
      console.error(err);
      alert('Network error submitting offer.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="offers-mgr-card">
      <div className="offers-mgr-header">
        <div>
          <h3 style={{ margin: 0, color: '#064e3b', fontSize: '1.2rem', fontFamily: 'Playfair Display, serif' }}>
            Privilege Commercial Offers & Catalogs
          </h3>
          <span style={{ fontSize: '0.72rem', color: '#64748b' }}>
            Publish discounts and products accessible via Member Privilege Cards
          </span>
        </div>
        <button className="btn-create-offer" onClick={() => handleOpenModal()}>
          <Plus size={15} />
          <span>Publish New Offer</span>
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#059669' }}>
          <Loader2 size={28} className="spin-loader" style={{ margin: '0 auto 8px' }} />
          <span>Loading offers...</span>
        </div>
      ) : offers.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8', border: '1.5px dashed #cbd5e1', borderRadius: '12px' }}>
          <Tag size={36} color="#cbd5e1" style={{ margin: '0 auto 8px' }} />
          <p style={{ margin: 0, fontSize: '0.86rem', fontWeight: 600 }}>No published offers found.</p>
          <span style={{ fontSize: '0.75rem' }}>Click "Publish New Offer" to list your first privilege deal.</span>
        </div>
      ) : (
        <div className="offers-grid-container">
          {offers.map(offer => {
            const firstImg = offer.images && offer.images[0] ? `${API_BASE_URL}${offer.images[0]}` : null;
            return (
              <div className="offer-item-card" key={offer.id}>
                <div className="offer-card-banner">
                  {firstImg ? (
                    <img src={firstImg} alt={offer.title} className="offer-banner-img" />
                  ) : (
                    <Tag size={36} color="#10b981" />
                  )}
                  <div className="offer-badge-float">{offer.discount_tag}</div>
                </div>

                <div className="offer-card-body">
                  <div>
                    <h4 className="offer-title-text">{offer.title}</h4>
                    <p className="offer-desc-snippet">{offer.description}</p>
                    <div className="offer-meta-tag-row">
                      <span className="tag-validity">
                        <Calendar size={11} />
                        <span>Valid: {offer.valid_until?.split('T')[0]}</span>
                      </span>
                      {offer.promo_code && (
                        <span className="tag-promo">CODE: {offer.promo_code}</span>
                      )}
                    </div>
                  </div>

                  <div className="offer-actions-footer">
                    <div>
                      {offer.offer_url && (
                        <a 
                          href={offer.offer_url} 
                          target="_blank" 
                          rel="noreferrer"
                          className="btn-icon-action" 
                          style={{ color: '#059669' }}
                        >
                          <ExternalLink size={12} />
                          <span>Link</span>
                        </a>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button className="btn-icon-action" onClick={() => handleOpenModal(offer)}>
                        <Edit3 size={12} />
                        <span>Edit</span>
                      </button>
                      <button className="btn-icon-action delete" onClick={() => handleDeleteOffer(offer.id)}>
                        <Trash2 size={12} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* CREATE / EDIT OFFER MODAL */}
      {modalOpen && (
        <div className="modal-overlay-dark">
          <div className="modal-content-box">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', borderBottom: '1.5px solid #f1f5f9', paddingBottom: '10px' }}>
              <h3 style={{ margin: 0, color: '#064e3b', fontSize: '1.15rem', fontFamily: 'Playfair Display, serif' }}>
                {editingOffer ? 'Edit Privilege Deal' : 'Publish New Privilege Offer'}
              </h3>
              <button onClick={() => setModalOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748b' }}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid-double">
                <div>
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                    Offer Headline / Title *
                  </label>
                  <input
                    type="text"
                    className="input-lux-offer"
                    placeholder="e.g. 20% Off Fine Diamond Making Charges"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                    Discount Tag Badge *
                  </label>
                  <input
                    type="text"
                    className="input-lux-offer"
                    placeholder="e.g. FLAT 25% OFF, BUY 1 GET 1"
                    value={formData.discount_tag}
                    onChange={(e) => setFormData({ ...formData, discount_tag: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-grid-double">
                <div>
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                    Promo / Coupon Code (Optional)
                  </label>
                  <input
                    type="text"
                    className="input-lux-offer"
                    placeholder="e.g. PRIVILEGE2026"
                    value={formData.promo_code}
                    onChange={(e) => setFormData({ ...formData, promo_code: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                    Sector / Category
                  </label>
                  <input
                    type="text"
                    className="input-lux-offer"
                    placeholder="e.g. Jewellery & Luxury"
                    value={formData.offer_category}
                    onChange={(e) => setFormData({ ...formData, offer_category: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-grid-double">
                <div>
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                    Valid From Date *
                  </label>
                  <input
                    type="date"
                    className="input-lux-offer"
                    value={formData.valid_from}
                    onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                    Valid Until Date *
                  </label>
                  <input
                    type="date"
                    className="input-lux-offer"
                    value={formData.valid_until}
                    onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                  Product Landing / Storefront Booking URL (Optional)
                </label>
                <input
                  type="url"
                  className="input-lux-offer"
                  placeholder="https://www.company.com/privilege-deal"
                  value={formData.offer_url}
                  onChange={(e) => setFormData({ ...formData, offer_url: e.target.value })}
                />
              </div>

              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                  Detailed Description *
                </label>
                <textarea
                  rows={2}
                  className="input-lux-offer"
                  placeholder="Elaborate what products/services qualify for this privilege discount."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                />
              </div>

              <div style={{ marginBottom: '14px' }}>
                <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: '#064e3b', marginBottom: '4px' }}>
                  Upload Offer Images (Up to 5)
                </label>
                <input
                  type="file"
                  multiple
                  accept="image/png, image/jpeg, image/webp"
                  onChange={(e) => setSelectedImages(Array.from(e.target.files))}
                  style={{ fontSize: '0.78rem' }}
                />
                {existingImages.length > 0 && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                    {existingImages.map((img, idx) => (
                      <div key={idx} style={{ position: 'relative', width: 44, height: 44, borderRadius: 6, overflow: 'hidden', border: '1px solid #cbd5e1' }}>
                        <img src={`${API_BASE_URL}${img}`} alt="prev" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        <button
                          type="button"
                          onClick={() => setExistingImages(existingImages.filter((_, i) => i !== idx))}
                          style={{ position: 'absolute', top: 0, right: 0, background: '#ef4444', color: '#fff', border: 'none', cursor: 'pointer', padding: 2 }}
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '11px',
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  color: '#ffffff',
                  fontWeight: 800,
                  fontSize: '0.84rem',
                  border: 'none',
                  borderRadius: '9px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px'
                }}
              >
                {submitting ? <Loader2 size={15} className="spin-loader" /> : <Save size={15} />}
                <span>{submitting ? 'Saving Offer...' : 'Save & Publish Deal'}</span>
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}